/**
 * Document Sensitivity Classifications
 *
 * Defines sensitivity tiers for documents to control access based on
 * user permissions and compliance requirements.
 *
 * @see Issue #293 - Permission System Enhancement
 */

import { Permission, type PermissionValue } from './permissions';

/**
 * Document sensitivity levels
 *
 * Used to classify documents and control access based on user permissions.
 * Higher sensitivity requires more permissions.
 */
export enum DocumentSensitivity {
  /** Public documents - visible to all authenticated users */
  PUBLIC = 'public',

  /** Internal documents - standard business documents */
  INTERNAL = 'internal',

  /** Confidential documents - PII, financial records */
  CONFIDENTIAL = 'confidential',

  /** Restricted documents - SSN, full credit reports, tax returns */
  RESTRICTED = 'restricted',
}

/**
 * Display names for sensitivity levels
 */
export const DOCUMENT_SENSITIVITY_NAMES: Record<DocumentSensitivity, string> = {
  [DocumentSensitivity.PUBLIC]: 'Public',
  [DocumentSensitivity.INTERNAL]: 'Internal',
  [DocumentSensitivity.CONFIDENTIAL]: 'Confidential',
  [DocumentSensitivity.RESTRICTED]: 'Restricted',
};

/**
 * Descriptions for each sensitivity level
 */
export const DOCUMENT_SENSITIVITY_DESCRIPTIONS: Record<DocumentSensitivity, string> = {
  [DocumentSensitivity.PUBLIC]: 'Documents visible to all team members',
  [DocumentSensitivity.INTERNAL]: 'Standard business documents requiring basic access',
  [DocumentSensitivity.CONFIDENTIAL]: 'Documents containing PII or financial information',
  [DocumentSensitivity.RESTRICTED]: 'Highly sensitive documents (SSN, credit reports, tax returns)',
};

/**
 * Permissions required for each sensitivity level
 *
 * Users must have ALL listed permissions to access documents at that level.
 */
export const SENSITIVITY_REQUIRED_PERMISSIONS: Record<DocumentSensitivity, PermissionValue[]> = {
  [DocumentSensitivity.PUBLIC]: [],
  [DocumentSensitivity.INTERNAL]: [Permission.documents.read],
  [DocumentSensitivity.CONFIDENTIAL]: [Permission.documents.read, Permission.documents.sensitiveRead],
  [DocumentSensitivity.RESTRICTED]: [Permission.documents.read, Permission.documents.restrictedRead],
};

/**
 * Color indicators for UI display
 */
export const DOCUMENT_SENSITIVITY_COLORS: Record<DocumentSensitivity, string> = {
  [DocumentSensitivity.PUBLIC]: 'green',
  [DocumentSensitivity.INTERNAL]: 'blue',
  [DocumentSensitivity.CONFIDENTIAL]: 'orange',
  [DocumentSensitivity.RESTRICTED]: 'red',
};

/**
 * Sensitivity levels in order of increasing restriction
 */
export const SENSITIVITY_ORDER: readonly DocumentSensitivity[] = [
  DocumentSensitivity.PUBLIC,
  DocumentSensitivity.INTERNAL,
  DocumentSensitivity.CONFIDENTIAL,
  DocumentSensitivity.RESTRICTED,
] as const;

/**
 * Check if sensitivity level A is at least as restrictive as level B
 */
export function isAtLeastAsRestrictive(sensitivityA: DocumentSensitivity, sensitivityB: DocumentSensitivity): boolean {
  const indexA = SENSITIVITY_ORDER.indexOf(sensitivityA);
  const indexB = SENSITIVITY_ORDER.indexOf(sensitivityB);
  return indexA >= indexB;
}

/**
 * Get required permissions for accessing a document at a sensitivity level
 */
export function getRequiredPermissionsForSensitivity(sensitivity: DocumentSensitivity): PermissionValue[] {
  return SENSITIVITY_REQUIRED_PERMISSIONS[sensitivity] || [];
}

/**
 * Check if a user with given permissions can access a document at a sensitivity level
 */
export function canAccessSensitivity(userPermissions: PermissionValue[], sensitivity: DocumentSensitivity): boolean {
  const required = getRequiredPermissionsForSensitivity(sensitivity);
  return required.every((permission) => userPermissions.includes(permission));
}
